
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ShieldCheck, ShieldX, Printer, ShoppingCart, Wrench, Zap } from "lucide-react";
import { MobileTooltipWrapper } from "@/components/ui/tooltip";

interface TutorialSidebarProps {
  category: string;
  subcategory: string;
  difficulty: number;
  requiresRoot: boolean;
  filters: string[];
  videoLink?: string;
  writtenGuide?: string;
}

const TutorialSidebar = ({
  category,
  subcategory,
  difficulty,
  requiresRoot,
  filters,
  videoLink,
  writtenGuide
}: TutorialSidebarProps) => {
  return (
    <div className="bg-card rounded-lg border p-6 sticky top-20">
      <h2 className="text-lg font-semibold mb-4">Tutorial Information</h2>
      
      <div className="space-y-4">
        <div>
          <p className="text-sm text-muted-foreground mb-1">Category:</p>
          <Badge variant="outline" className="capitalize">{category}</Badge>
        </div>
        
        <div>
          <p className="text-sm text-muted-foreground mb-1">Subcategory:</p>
          <Badge variant="outline" className="capitalize">{subcategory}</Badge>
        </div>
        
        <div>
          <p className="text-sm text-muted-foreground mb-1">Required parts:</p>
          <div className="flex flex-wrap gap-3 pt-2">
            {requiresRoot && (
              <MobileTooltipWrapper content="Root access is needed to make changes to the printer configuration file">
                <div className="flex items-center gap-1 text-red-500">
                  <ShieldX size={16} />
                </div>
              </MobileTooltipWrapper>
            )}
            {!requiresRoot && (
              <MobileTooltipWrapper content="No changes to the printer configuration file are needed">
                <div className="flex items-center gap-1 text-gray-500 dark:text-gray-500">
                  <ShieldCheck size={16} />
                </div>
              </MobileTooltipWrapper>
            )}
            {filters.includes("wiring") && (
              <MobileTooltipWrapper content="Not plug and play - requires wiring">
                <div className="flex items-center gap-1 text-yellow-400">
                  <Zap size={16} />
                </div>
              </MobileTooltipWrapper>
            )}
            {filters.includes("printed") && (
              <MobileTooltipWrapper content="This is a printed part or requires printed parts">
                <div className="flex items-center gap-1 text-blue-500">
                  <Printer size={16} />
                </div>
              </MobileTooltipWrapper>
            )}
            {filters.includes("tools") && (
              <MobileTooltipWrapper content="Additional tools which did not come with your printer are required">
                <div className="flex items-center gap-1 text-purple-600">
                  <Wrench size={16} />
                </div>
              </MobileTooltipWrapper>
            )}
            {filters.includes("purchased") && (
              <MobileTooltipWrapper content="This is a purchased part or requires purchased parts">
                <div className="flex items-center gap-1 text-lime-600 dark:text-green-400">
                  <ShoppingCart size={16} />
                </div>
              </MobileTooltipWrapper>
            )}
          </div>
        </div>
        
        <div className="pt-4 space-y-3">
          {videoLink && (
            <Button variant="outline" size="sm" className="w-full whitespace-normal h-auto py-1.5 min-h-9 text-center" asChild>
              <a href={videoLink} target="_blank" rel="noopener noreferrer">
                Video Guide
              </a>
            </Button>
          )}
          
          {writtenGuide && (
            <Button variant="default" size="sm" className="w-full whitespace-normal h-auto py-1.5 min-h-9 text-center" asChild>
              <a href={writtenGuide} target="_blank" rel="noopener noreferrer">
                Written Guide
              </a>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TutorialSidebar;
